/**
 * Institution admin provisioning helpers (FE #27 / BE #61) — kept out of the
 * component files so React fast refresh stays happy.
 */
import type { InstitutionAdminProvision, InstitutionAdminResult } from '../services/api'
import { mailProblemMessage, ut } from './usersI18n'

export interface AdminProvisionState {
  email: string
  first_name: string
  last_name: string
  language: 'sk' | 'en'
}

export const emptyAdminProvision = (email = ''): AdminProvisionState => ({
  email,
  first_name: '',
  last_name: '',
  language: 'sk',
})

/** Build the provisioning payload; empty optional fields are omitted. */
export const toProvisionPayload = (
  admin: AdminProvisionState,
  fallbackEmail: string,
  assignExisting = false,
): InstitutionAdminProvision => {
  const payload: InstitutionAdminProvision = {
    create: true,
    email: (admin.email.trim() || fallbackEmail.trim()) || undefined,
    language: admin.language,
    assign_existing: assignExisting,
  }
  if (admin.first_name.trim()) payload.first_name = admin.first_name.trim()
  if (admin.last_name.trim()) payload.last_name = admin.last_name.trim()
  return payload
}

export interface OutcomeMessage {
  severity: 'success' | 'error'
  text: string
}

/** Human-readable outcome of a provisioning call (never hides a mail problem). */
export function describeAdminResult(result: InstitutionAdminResult): OutcomeMessage {
  const problem = mailProblemMessage(result.email, result)
  if (problem) return { severity: 'error', text: problem }
  if (result.action === 'assigned') {
    return {
      severity: 'success',
      text:
        result.notification === 'invite'
          ? ut('assignedInviteSentTo', { email: result.email })
          : ut('assignmentSentTo', { email: result.email }),
    }
  }
  return { severity: 'success', text: ut('inviteSentTo', { email: result.email }) }
}

